// ===========================================
// COMPONENTE: PlanPagoDialog
// ===========================================
// Dialog para marcar un vencimiento en plan de pago

import { useState } from 'react'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { FileSpreadsheet, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { getImpuestoColorClasses } from '@/lib/impuestoColorResolver'
import {
  usePlanPagoForm,
  getFieldError,
  type PlanPagoFormValues,
} from './forms'
import type { MarcarPlanPagoData } from '@/types/vencimientos'
import { useStore } from '@tanstack/react-form'
import { FormDialogContent } from '@/components/ui/form-dialog-content'

interface VencimientoParaPlanPago {
  id: string | null
  tipo_impuesto: string | null
  cliente_nombre: string | null
  periodo: string | null
  fecha_vencimiento: string | null
}

interface PlanPagoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vencimiento: VencimientoParaPlanPago | null
  onConfirmar: (id: string, datos: MarcarPlanPagoData) => Promise<void>
}

export function PlanPagoDialog({
  open,
  onOpenChange,
  vencimiento,
  onConfirmar,
}: PlanPagoDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { form } = usePlanPagoForm({
    onSubmit: async (value: PlanPagoFormValues) => {
      if (!vencimiento?.id) return

      setIsSubmitting(true)
      try {
        await onConfirmar(vencimiento.id, {
          cuotas: value.cuotas,
          referencia: value.referencia,
          observaciones: value.observaciones,
        })
        form.reset()
        onOpenChange(false)
      } catch (error) {
        console.error('Error al marcar plan de pago:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const isDirty = useStore(form.store, (s) => s.isDirty)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) form.reset()
    onOpenChange(newOpen)
  }

  if (!vencimiento) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => handleOpenChange(false)} className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-violet-600" />
            Plan de Pago
          </DialogTitle>
          <DialogDescription>
            Registrar adhesión a plan de pagos
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/50 p-3 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            {vencimiento.tipo_impuesto && (
              <Badge variant="outline" className={getImpuestoColorClasses(vencimiento.tipo_impuesto)}>
                {vencimiento.tipo_impuesto}
              </Badge>
            )}
            <span className="text-sm font-medium">{vencimiento.cliente_nombre}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            <span>Periodo: {vencimiento.periodo}</span>
            {vencimiento.fecha_vencimiento && (
              <>
                <span className="mx-2">|</span>
                <span>Vencimiento: {format(new Date(vencimiento.fecha_vencimiento), 'dd/MM/yyyy')}</span>
              </>
            )}
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field name="cuotas">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Cantidad de Cuotas</Label>
                <Input
                  id={field.name}
                  type="number"
                  min={1}
                  max={120}
                  placeholder="Ej: 12"
                  value={field.state.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    field.handleChange(val === '' ? null : parseInt(val, 10))
                  }}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="referencia">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Referencia del Plan</Label>
                <Input
                  id={field.name}
                  placeholder="Ej: Plan Mis Facilidades - Nro. 123456"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="observaciones">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Observaciones</Label>
                <Textarea
                  id={field.name}
                  placeholder="Notas adicionales..."
                  className="resize-none"
                  rows={2}
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-violet-600 hover:bg-violet-700">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar Plan
            </Button>
          </DialogFooter>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
